import { addScript, localStorage } from '@tunein/web-utils';
import { noop } from 'es-toolkit';
import { extractLotameInterestsFromGuideItem } from './utils';
const LOTAME_CLIENT_ID = 10306;
const LOTAME_GLOBAL = `lotame_${LOTAME_CLIENT_ID}`;
const LOTAME_AUDIENCE_LIMIT = 40;
const LOTAME_SCRIPT_ID = 'TI_LOTAME_SCRIPT';
const AUDIENCES = 'LOTAME_LIGHTNING_AUDIENCES';
const LOTAME_FETCH_AUDIENCE_ERROR = 'lotame.fetch.audience.error';
const LOTAME_COMMAND_QUEUE_ERROR = 'lotame.command.queue.error';
class Lotame {
    #options;
    get #lotame() {
        return window[LOTAME_GLOBAL];
    }
    set #lotame(lotame) {
        window[LOTAME_GLOBAL] = lotame;
    }
    #preConfigureLotame() {
        this.#lotame = {
            config: {
                clientId: LOTAME_CLIENT_ID,
                audienceLocalStorage: AUDIENCES,
                onProfileReady: this.#updateAudiences,
            },
            data: {},
            cmd: [],
        };
    }
    #updateAudiences = (profile) => {
        const audiences = profile ? this.#getAudiences(profile) : this.#getCachedAudiences();
        this.#options?.updateLotameAudiences?.(audiences);
    };
    #getAudiences(profile) {
        try {
            return profile.getAudiences(LOTAME_AUDIENCE_LIMIT) || [];
        }
        catch (error) {
            this.#options?.onError({
                message: LOTAME_FETCH_AUDIENCE_ERROR,
                context: { error },
            });
        }
        return [];
    }
    #getCachedAudiences() {
        const audiences = localStorage.get(AUDIENCES);
        return audiences?.split(',')?.slice(0, LOTAME_AUDIENCE_LIMIT) || [];
    }
    #addLotameInterests(interests) {
        this.#lotame.collect(this.#buildDataObject(interests));
    }
    #buildDataObject(interests) {
        return {
            behaviors: {
                int: interests,
            },
        };
    }
    #signalPageChange(interests) {
        this.#lotame.page(this.#buildDataObject(interests));
    }
    #setLotameConsent(...args) {
        this.#lotame.setConsent(...args);
    }
    #queueLotameCommand(command, ...args) {
        try {
            this.#lotame.cmd.push(command.bind(this, ...args));
        }
        catch (error) {
            this.#options?.onError({
                message: LOTAME_COMMAND_QUEUE_ERROR,
                context: { error },
            });
        }
    }
    init(options) {
        this.#options = options;
        this.#preConfigureLotame();
        this.#updateAudiences();
        addScript({
            id: LOTAME_SCRIPT_ID,
            src: `https://tags.crwdcntrl.net/lt/c/${LOTAME_CLIENT_ID}/lt.min.js`,
            async: true,
        });
    }
    setConsent(isUserOptedIn) {
        const consents = {
            analytics: isUserOptedIn,
            crossdevice: isUserOptedIn,
            datasharing: isUserOptedIn,
            targeting: isUserOptedIn,
        };
        this.#queueLotameCommand(this.#setLotameConsent, noop, LOTAME_CLIENT_ID, consents);
    }
    signalPageChange(guideItem) {
        const interests = extractLotameInterestsFromGuideItem(guideItem);
        this.#queueLotameCommand(this.#signalPageChange, interests);
    }
    addInterests(guideItem) {
        const interests = extractLotameInterestsFromGuideItem(guideItem);
        this.#queueLotameCommand(this.#addLotameInterests, interests);
    }
    getAudiences() {
        return this.#getCachedAudiences();
    }
}
export const lotame = new Lotame();
